import {
  Box,
  Typography,
  Button,
  Card,
  CardContent,
  CircularProgress,
  Alert,
  IconButton,
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  TextField,
  Menu,
  MenuItem,
  Chip,
  Grid,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  Paper,
  InputAdornment,
  TablePagination,
  Tooltip,
  Select,
  FormControl,
  InputLabel,
  FormControlLabel,
  Checkbox,
} from '@mui/material';
import { apiService } from '../services/api';
import { API_ENDPOINTS } from '../constants/api';
import { useState, useEffect, useCallback, useMemo } from 'react';
import AppLayout from '../components/AppLayout';
import { RoleTemplateState } from '../types';
import type { User } from '../types';
import AddIcon from '@mui/icons-material/Add';
import EditIcon from '@mui/icons-material/Edit';
import DeleteIcon from '@mui/icons-material/Delete';
import MoreVertIcon from '@mui/icons-material/MoreVert';
import AssessmentIcon from '@mui/icons-material/Assessment';
import SearchIcon from '@mui/icons-material/Search';
import VisibilityIcon from '@mui/icons-material/Visibility';
import CloseIcon from '@mui/icons-material/Close';
import CheckCircleIcon from '@mui/icons-material/CheckCircle';
import SendIcon from '@mui/icons-material/Send';
import BalanceIcon from '@mui/icons-material/Balance';
import { getApiErrorMessage } from '../utils/apiError';
import { couldNotLoad, couldNotSubmit, couldNotUpdate, couldNotDelete, couldNotCreate, couldNotApprove, couldNotAdd } from '../utils/userFacingCopy';
import { successCopy } from '../utils/successCopy';
import { useSuccessSnackbar } from '../contexts/SuccessSnackbarContext';
import { SortableTableHeader } from '../utils/sortableTableHeader';

interface ObjectiveMeasure {
  id: string;
  name: string;
  measurementMethod: string;
  level1Criteria: string;
  level2Criteria: string;
  level3Criteria: string;
  weighting: number;
  roleTemplateId?: string;
  userId?: string;
}

interface RoleTemplate {
  id: string;
  name: string;
  description: string;
  state: RoleTemplateState | number | string; // Allow number or string since API may serialize enum as string
  objectiveMeasures: ObjectiveMeasure[];
}

interface ReviewCycleOption {
  id: string;
  name: string;
}

interface CreateRoleTemplateForm {
  name: string;
  description: string;
  objectiveMeasures: CreateObjectiveMeasureForm[];
}

interface CreateObjectiveMeasureForm {
  name: string;
  measurementMethod: string;
  level1Criteria: string;
  level2Criteria: string;
  level3Criteria: string;
  weighting: number;
}

export default function RoleTemplatesPage() {
  const [roleTemplates, setRoleTemplates] = useState<RoleTemplate[]>([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string>('');
  const [createDialogOpen, setCreateDialogOpen] = useState(false);
  const [editDialogOpen, setEditDialogOpen] = useState(false);
  const [deleteDialogOpen, setDeleteDialogOpen] = useState(false);
  const [templateToDelete, setTemplateToDelete] = useState<RoleTemplate | null>(null);
  const [selectedTemplate, setSelectedTemplate] = useState<RoleTemplate | null>(null);
  const [menuAnchor, setMenuAnchor] = useState<null | HTMLElement>(null);
  const [menuTemplateId, setMenuTemplateId] = useState<string | null>(null);
  const [creating, setCreating] = useState(false);
  const [updating, setUpdating] = useState(false);
  const [deleting, setDeleting] = useState<string | null>(null);
  const [addMeasureDialogOpen, setAddMeasureDialogOpen] = useState(false);
  const [editMeasureDialogOpen, setEditMeasureDialogOpen] = useState(false);
  const [deleteMeasureDialogOpen, setDeleteMeasureDialogOpen] = useState(false);
  const [measureToDelete, setMeasureToDelete] = useState<string | null>(null);
  const [selectedTemplateId, setSelectedTemplateId] = useState<string | null>(null);
  const [selectedMeasure, setSelectedMeasure] = useState<ObjectiveMeasure | null>(null);
  const [newMeasure, setNewMeasure] = useState<CreateObjectiveMeasureForm>({
    name: '',
    measurementMethod: '',
    level1Criteria: '',
    level2Criteria: '',
    level3Criteria: '',
    weighting: 0,
  });
  const [editMeasure, setEditMeasure] = useState<CreateObjectiveMeasureForm>({
    name: '',
    measurementMethod: '',
    level1Criteria: '',
    level2Criteria: '',
    level3Criteria: '',
    weighting: 0,
  });
  const [createForm, setCreateForm] = useState<CreateRoleTemplateForm>({
    name: '',
    description: '',
    objectiveMeasures: [],
  });
  const [editForm, setEditForm] = useState<{ name: string; description: string }>({
    name: '',
    description: '',
  });
  const [createError, setCreateError] = useState<string>('');
  const [editError, setEditError] = useState<string>('');
  const [measureError, setMeasureError] = useState<string>('');
  const [searchQuery, setSearchQuery] = useState('');
  const [stateFilter, setStateFilter] = useState<string>('all');
  const [page, setPage] = useState(0);
  const [rowsPerPage, setRowsPerPage] = useState(25);
  const [sortBy, setSortBy] = useState<'name'>('name');
  const [sortDirection, setSortDirection] = useState<'asc' | 'desc'>('asc');
  const [viewMeasuresDialogOpen, setViewMeasuresDialogOpen] = useState(false);
  const [viewingTemplate, setViewingTemplate] = useState<RoleTemplate | null>(null);
  const [userCounts, setUserCounts] = useState<Record<string, number>>({});
  const [applyWeightsDialogOpen, setApplyWeightsDialogOpen] = useState(false);
  const [applyWeightsTemplate, setApplyWeightsTemplate] = useState<RoleTemplate | null>(null);
  const [reviewCycles, setReviewCycles] = useState<ReviewCycleOption[]>([]);
  const [applyWeightsCycleId, setApplyWeightsCycleId] = useState('');
  const [includeClosedReviews, setIncludeClosedReviews] = useState(true);
  const [applyingWeights, setApplyingWeights] = useState(false);
  const [applyWeightsError, setApplyWeightsError] = useState('');
  const showSuccess = useSuccessSnackbar();

  const loadRoleTemplates = useCallback(async () => {
    try {
      setLoading(true);
      setError('');
      
      // Load role templates and users in parallel
      const [templatesResponse, usersResponse] = await Promise.all([
        apiService.get<RoleTemplate[]>('/roles'),
        apiService.get<User[]>(API_ENDPOINTS.USER_MANAGEMENT.BASE).catch(() => ({ data: [] as User[] })), // Fallback to empty array if not admin
      ]);
      
      setRoleTemplates(templatesResponse.data);
      
      // Calculate user counts for each template
      const counts: Record<string, number> = {};
      templatesResponse.data.forEach((template) => {
        counts[template.id] = usersResponse.data.filter(
          (user) => user.roleTemplateId === template.id && user.isActive
        ).length;
      });
      setUserCounts(counts);
    } catch (err: unknown) {
            setError(getApiErrorMessage(err, couldNotLoad('role templates')));
    } finally {
      setLoading(false);
    }
  }, []);

  useEffect(() => {
    loadRoleTemplates();
  }, [loadRoleTemplates]);

  const handleCreate = async () => {
    if (!createForm.name.trim()) {
      setCreateError('Name is required');
      return;
    }

    // Validate all objective measures have valid weighting
    for (const measure of createForm.objectiveMeasures) {
      if (measure.weighting < 0 || measure.weighting > 1) {
        setCreateError(`Weighting must be between 0 and 1 for measure "${measure.name}"`);
        return;
      }
    }

    try {
      setCreating(true);
      setCreateError('');
      await apiService.post('/roles', createForm);
      setCreateDialogOpen(false);
      setCreateForm({ name: '', description: '', objectiveMeasures: [] });
      loadRoleTemplates();
    } catch (err: unknown) {
            setCreateError(getApiErrorMessage(err, couldNotCreate('the role template')));
    } finally {
      setCreating(false);
    }
  };

  const handleEdit = async () => {
    if (!selectedTemplate || !editForm.name.trim()) {
      setEditError('Name is required');
      return;
    }

    try {
      setUpdating(true);
      setEditError('');
      await apiService.put(`/roles/${selectedTemplate.id}`, editForm);
      setEditDialogOpen(false);
      setSelectedTemplate(null);
      loadRoleTemplates();
    } catch (err: unknown) {
            setEditError(getApiErrorMessage(err, couldNotUpdate('the role template')));
    } finally {
      setUpdating(false);
    }
  };

  const handleSubmitForApproval = async (templateId: string) => {
    try {
      await apiService.post(`${API_ENDPOINTS.ROLES.SUBMIT_FOR_APPROVAL(templateId)}`);
      await loadRoleTemplates();
      setMenuAnchor(null);
    } catch (err: unknown) {
            setError(getApiErrorMessage(err, couldNotSubmit('the template for approval')));
    }
  };

  const handleApprove = async (templateId: string) => {
    try {
      await apiService.post(`${API_ENDPOINTS.ROLES.APPROVE(templateId)}`);
      await loadRoleTemplates();
      setMenuAnchor(null);
    } catch (err: unknown) {
            setError(getApiErrorMessage(err, couldNotApprove('the template')));
    }
  };

  const handleOpenApplyWeights = async (template: RoleTemplate) => {
    setApplyWeightsTemplate(template);
    setApplyWeightsCycleId('');
    setIncludeClosedReviews(true);
    setApplyWeightsError('');
    setApplyWeightsDialogOpen(true);
    setMenuAnchor(null);
    try {
      const response = await apiService.get<ReviewCycleOption[]>(API_ENDPOINTS.REVIEW_CYCLES.BASE);
      setReviewCycles(response.data.map((c) => ({ id: c.id, name: c.name })));
    } catch (err: unknown) {
      setApplyWeightsError(getApiErrorMessage(err, couldNotLoad('review cycles')));
    }
  };

  const handleApplyWeightsConfirm = async () => {
    if (!applyWeightsTemplate || !applyWeightsCycleId) {
      setApplyWeightsError('Choose a review cycle to continue.');
      return;
    }

    try {
      setApplyingWeights(true);
      setApplyWeightsError('');
      const response = await apiService.post<{ reviewsUpdated: number; objectiveRatingsUpdated: number }>(
        API_ENDPOINTS.ROLES.APPLY_OBJECTIVE_WEIGHTS(applyWeightsTemplate.id),
        {
          reviewCycleId: applyWeightsCycleId,
          includeClosedReviews,
        }
      );
      showSuccess(successCopy.objectiveWeightsApplied(response.data.reviewsUpdated));
      setApplyWeightsDialogOpen(false);
      setApplyWeightsTemplate(null);
    } catch (err: unknown) {
      setApplyWeightsError(getApiErrorMessage(err, couldNotUpdate('review weightings')));
    } finally {
      setApplyingWeights(false);
    }
  };

  const handleDeleteClick = (templateId: string) => {
    const template = roleTemplates.find((t) => t.id === templateId);
    if (template) {
      setTemplateToDelete(template);
      setDeleteDialogOpen(true);
      setMenuAnchor(null);
    }
  };

  const handleDeleteConfirm = async () => {
    if (!templateToDelete) return;

    const assignedUserCount = userCounts[templateToDelete.id] ?? 0;
    if (assignedUserCount > 0) {
      // Should not happen if UI is correct, but double-check
      setError(`Cannot delete template "${templateToDelete.name}" because it is assigned to ${assignedUserCount} user${assignedUserCount !== 1 ? 's' : ''}. Please unassign all users before deleting.`);
      setDeleteDialogOpen(false);
      setTemplateToDelete(null);
      return;
    }

    try {
      setDeleting(templateToDelete.id);
      await apiService.delete(`/roles/${templateToDelete.id}`);
      await loadRoleTemplates();
      setDeleteDialogOpen(false);
      setTemplateToDelete(null);
    } catch (err: unknown) {
            setError(getApiErrorMessage(err, couldNotDelete('the role template')));
    } finally {
      setDeleting(null);
    }
  };

  const handleMenuOpen = (event: React.MouseEvent<HTMLElement>, templateId: string) => {
    setMenuAnchor(event.currentTarget);
    setMenuTemplateId(templateId);
  };

  const handleMenuClose = () => {
    setMenuAnchor(null);
    setMenuTemplateId(null);
  };

  const handleOpenEdit = (template: RoleTemplate) => {
    setSelectedTemplate(template);
    setEditForm({
      name: template.name,
      description: template.description,
    });
    setEditDialogOpen(true);
    setMenuAnchor(null);
  };

  const handleAddObjectiveMeasure = () => {
    setCreateForm({
      ...createForm,
      objectiveMeasures: [
        ...createForm.objectiveMeasures,
        {
          name: '',
          measurementMethod: '',
          level1Criteria: '',
          level2Criteria: '',
          level3Criteria: '',
          weighting: 0,
        },
      ],
    });
  };

  const handleRemoveObjectiveMeasure = (index: number) => {
    setCreateForm({
      ...createForm,
      objectiveMeasures: createForm.objectiveMeasures.filter((_, i) => i !== index),
    });
  };

  const handleObjectiveMeasureChange = (index: number, field: keyof CreateObjectiveMeasureForm, value: string | number) => {
    const updated = [...createForm.objectiveMeasures];
    updated[index] = { ...updated[index], [field]: value };
    setCreateForm({ ...createForm, objectiveMeasures: updated });
  };

  const handleOpenAddMeasure = (templateId: string) => {
    setSelectedTemplateId(templateId);
    setNewMeasure({
      name: '',
      measurementMethod: '',
      level1Criteria: '',
      level2Criteria: '',
      level3Criteria: '',
      weighting: 0,
    });
    setMeasureError('');
    setAddMeasureDialogOpen(true);
  };

  const handleAddObjectiveMeasureToTemplate = async () => {
    if (!selectedTemplateId || !newMeasure.name.trim()) {
      setMeasureError('Name is required');
      return;
    }
    if (newMeasure.weighting < 0 || newMeasure.weighting > 1) {
      setMeasureError('Weighting must be between 0 and 1');
      return;
    }

    try {
      setMeasureError('');
      await apiService.post(`/roles/${selectedTemplateId}/objective-measures`, newMeasure);
      setAddMeasureDialogOpen(false);
      setSelectedTemplateId(null);
      setNewMeasure({
        name: '',
        measurementMethod: '',
        level1Criteria: '',
        level2Criteria: '',
        level3Criteria: '',
        weighting: 0,
      });
      loadRoleTemplates();
    } catch (err: unknown) {
            setMeasureError(getApiErrorMessage(err, couldNotAdd('the objective measure')));
    }
  };

  const handleOpenEditMeasure = (measure: ObjectiveMeasure) => {
    setSelectedMeasure(measure);
    setEditMeasure({
      name: measure.name,
      measurementMethod: measure.measurementMethod,
      level1Criteria: measure.level1Criteria,
      level2Criteria: measure.level2Criteria,
      level3Criteria: measure.level3Criteria,
      weighting: measure.weighting,
    });
    setMeasureError('');
    setEditMeasureDialogOpen(true);
  };

  const handleUpdateObjectiveMeasure = async () => {
    if (!selectedMeasure || !editMeasure.name.trim()) {
      setMeasureError('Name is required');
      return;
    }
    if (editMeasure.weighting < 0 || editMeasure.weighting > 1) {
      setMeasureError('Weighting must be between 0 and 1');
      return;
    }

    try {
      setMeasureError('');
      await apiService.put(`/roles/objective-measures/${selectedMeasure.id}`, editMeasure);
      setEditMeasureDialogOpen(false);
      setSelectedMeasure(null);
      loadRoleTemplates();
    } catch (err: unknown) {
            setMeasureError(getApiErrorMessage(err, couldNotUpdate('the objective measure')));
    }
  };

  const handleDeleteObjectiveMeasureClick = (measureId: string) => {
    setMeasureToDelete(measureId);
    setDeleteMeasureDialogOpen(true);
  };

  const handleDeleteObjectiveMeasure = async () => {
    if (!measureToDelete) return;

    setDeleteMeasureDialogOpen(false);
    try {
      await apiService.delete(`/roles/objective-measures/${measureToDelete}`);
      if (viewingTemplate) {
        const updated = await apiService.get<RoleTemplate>(`${API_ENDPOINTS.ROLES.BASE}/${viewingTemplate.id}`);
        setViewingTemplate(updated.data);
      }
      await loadRoleTemplates();
      setMeasureToDelete(null);
    } catch (err: unknown) {
            setError(getApiErrorMessage(err, couldNotDelete('the objective measure')));
      setMeasureToDelete(null);
    }
  };

  // Get state label helper
  const getStateLabel = (state: RoleTemplateState | number | string): string => {
    // Handle string values (API uses JsonStringEnumConverter)
    if (typeof state === 'string') {
      switch (state) {
        case 'Draft':
          return 'Draft';
        case 'PendingApproval':
          return 'Pending Approval';
        case 'Approved':
          return 'Approved';
        default:
          return `Unknown (${state})`;
      }
    }
    
    // Handle numeric and enum values
    const stateValue = typeof state === 'number' ? state : (state as number);
    switch (stateValue) {
      case RoleTemplateState.Draft:
      case 1:
        return 'Draft';
      case RoleTemplateState.PendingApproval:
      case 2:
        return 'Pending Approval';
      case RoleTemplateState.Approved:
      case 3:
        return 'Approved';
      default:
        return `Unknown (${stateValue})`;
    }
  };

  // Get state color helper
  const getStateColor = (state: RoleTemplateState | number | string): 'default' | 'warning' | 'success' => {
    // Handle string values (API uses JsonStringEnumConverter)
    if (typeof state === 'string') {
      switch (state) {
        case 'Draft':
          return 'default';
        case 'PendingApproval':
          return 'warning';
        case 'Approved':
          return 'success';
        default:
          return 'default';
      }
    }
    
    // Handle numeric and enum values
    const stateValue = typeof state === 'number' ? state : (state as number);
    switch (stateValue) {
      case RoleTemplateState.Draft:
      case 1:
        return 'default';
      case RoleTemplateState.PendingApproval:
      case 2:
        return 'warning';
      case RoleTemplateState.Approved:
      case 3:
        return 'success';
      default:
        return 'default';
    }
  };

  // Filter and sort role templates based on search query and state
  const filteredRoleTemplates = useMemo(() => {
    let filtered = roleTemplates;

    // Apply state filter
    if (stateFilter !== 'all') {
      const stateValue = parseInt(stateFilter, 10);
      filtered = filtered.filter((template) => {
        // Handle string values (API uses JsonStringEnumConverter)
        if (typeof template.state === 'string') {
          const stringToNumber: Record<string, number> = {
            'Draft': 1,
            'PendingApproval': 2,
            'Approved': 3,
          };
          return stringToNumber[template.state] === stateValue;
        }
        // Handle numeric and enum values
        const templateState = typeof template.state === 'number' ? template.state : (template.state as unknown as number);
        return templateState === stateValue;
      });
    }

    // Apply search query filter
    if (searchQuery.trim()) {
      const query = searchQuery.toLowerCase();
      filtered = filtered.filter(
        (template) =>
          template.name.toLowerCase().includes(query) ||
          template.description.toLowerCase().includes(query) ||
          template.objectiveMeasures.some((measure) => measure.name.toLowerCase().includes(query))
      );
    }

    // Apply sorting
    const sorted = [...filtered].sort((a, b) => {
      if (sortBy === 'name') {
        const nameA = a.name.toLowerCase();
        const nameB = b.name.toLowerCase();
        if (sortDirection === 'asc') {
          return nameA.localeCompare(nameB);
        } else {
          return nameB.localeCompare(nameA);
        }
      }
      return 0;
    });

    return sorted;
  }, [roleTemplates, searchQuery, stateFilter, sortBy, sortDirection]);

  const handleSort = (column: 'name') => {
    if (sortBy === column) {
      // Toggle direction if clicking the same column
      setSortDirection(sortDirection === 'asc' ? 'desc' : 'asc');
    } else {
      // Set new column and default to ascending
      setSortBy(column);
      setSortDirection('asc');
    }
  };

  // Paginate filtered results
  const paginatedRoleTemplates = useMemo(() => {
    const startIndex = page * rowsPerPage;
    return filteredRoleTemplates.slice(startIndex, startIndex + rowsPerPage);
  }, [filteredRoleTemplates, page, rowsPerPage]);

  const handlePageChange = (_event: unknown, newPage: number) => {
    setPage(newPage);
  };

  const handleRowsPerPageChange = (event: React.ChangeEvent<HTMLInputElement>) => {
    setRowsPerPage(parseInt(event.target.value, 10));
    setPage(0);
  };

  if (loading) {
    return (
      <Box sx={{ display: 'flex', justifyContent: 'center', alignItems: 'center', minHeight: '100vh', bgcolor: '#f5f7fa' }}>
        <CircularProgress size={60} />
      </Box>
    );
  }

  return (
    <AppLayout>
      <Box sx={{ maxWidth: '1400px', mx: 'auto' }}>
        <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', mb: 3 }}>
          <Box sx={{ display: 'flex', alignItems: 'center', gap: 2 }}>
            <AssessmentIcon sx={{ fontSize: 32, color: 'primary.main', alignSelf: 'flex-start', mt: 0.5 }} />
            <Typography variant="h4" component="h1" fontWeight="bold" sx={{ mb: 0 }}>
              Role Templates Management
            </Typography>
          </Box>
          <Button
            variant="contained"
            startIcon={<AddIcon />}
            onClick={() => setCreateDialogOpen(true)}
            sx={{ textTransform: 'none' }}
          >
            Create Role Template
          </Button>
        </Box>

        <Alert severity="info" sx={{ mb: 3 }}>
          Role templates define the performance evaluation criteria for different positions. Each template includes objective and behavioural measures that are used during performance reviews. Templates must be approved before they can be assigned to users.
        </Alert>

        {error && (
          <Alert severity="error" sx={{ mb: 3 }} onClose={() => setError('')}>
            {error}
          </Alert>
        )}

        <Card variant="outlined" sx={{ mb: 3 }}>
          <CardContent>
            <Box sx={{ display: 'flex', gap: 2 }}>
              <TextField
                fullWidth
                label="Search role templates"
                placeholder="Search by name, description, or objective measure..."
                value={searchQuery}
                onChange={(e) => {
                  setSearchQuery(e.target.value);
                  setPage(0); // Reset to first page when searching
                }}
                InputProps={{
                  startAdornment: (
                    <InputAdornment position="start">
                      <SearchIcon />
                    </InputAdornment>
                  ),
                }}
              />
              <FormControl sx={{ minWidth: 200 }}>
                <InputLabel>State</InputLabel>
                <Select
                  value={stateFilter}
                  label="State"
                  onChange={(e) => {
                    setStateFilter(e.target.value);
                    setPage(0); // Reset to first page when filtering
                  }}
                >
                  <MenuItem value="all">All States</MenuItem>
                  <MenuItem value={RoleTemplateState.Draft.toString()}>Draft</MenuItem>
                  <MenuItem value={RoleTemplateState.PendingApproval.toString()}>Pending Approval</MenuItem>
                  <MenuItem value={RoleTemplateState.Approved.toString()}>Approved</MenuItem>
                </Select>
              </FormControl>
            </Box>
            {(searchQuery.trim() !== '' || stateFilter !== 'all') && (
              <Box sx={{ display: 'flex', gap: 1, flexWrap: 'wrap', mt: 2, pt: 2, borderTop: '1px solid', borderColor: 'divider' }}>
                {searchQuery.trim() !== '' && (
                  <Chip
                    label={`Search: "${searchQuery}"`}
                    onDelete={() => {
                      setSearchQuery('');
                      setPage(0);
                    }}
                    size="small"
                    color="primary"
                  />
                )}
                {stateFilter !== 'all' && (
                  <Chip
                    label={`State: ${
                      stateFilter === RoleTemplateState.Draft.toString() ? 'Draft' :
                      stateFilter === RoleTemplateState.PendingApproval.toString() ? 'Pending Approval' :
                      stateFilter === RoleTemplateState.Approved.toString() ? 'Approved' : stateFilter
                    }`}
                    onDelete={() => {
                      setStateFilter('all');
                      setPage(0);
                    }}
                    size="small"
                    color="info"
                  />
                )}
              </Box>
            )}
          </CardContent>
        </Card>

        {loading ? (
          <Box sx={{ display: 'flex', justifyContent: 'center', py: 4 }}>
            <CircularProgress />
          </Box>
        ) : filteredRoleTemplates.length === 0 ? (
          <Card variant="outlined">
            <CardContent>
              <Box sx={{ textAlign: 'center', py: 4 }}>
                <AssessmentIcon sx={{ fontSize: 64, color: 'text.secondary', mb: 2 }} />
                <Typography variant="h6" color="text.secondary" gutterBottom>
                  {(() => {
                    const hasActiveFilters = searchQuery.trim() || stateFilter !== 'all';
                    return hasActiveFilters ? 'No role templates match your search or filters.' : 'No role templates';
                  })()}
                </Typography>
                <Typography variant="body2" color="text.secondary">
                  {(() => {
                    const hasActiveFilters = searchQuery.trim() || stateFilter !== 'all';
                    if (hasActiveFilters) {
                      return 'Try adjusting your search or filter criteria.';
                    }
                    return 'Create your first role template to get started';
                  })()}
                </Typography>
              </Box>
            </CardContent>
          </Card>
        ) : (
          <>
            <TableContainer component={Paper} variant="outlined">
              <Table>
                <TableHead>
                  <TableRow>
                    <TableCell>
                      <SortableTableHeader
                        label="Name"
                        active={sortBy === 'name'}
                        direction={sortBy === 'name' ? sortDirection : 'asc'}
                        onClick={() => handleSort('name')}
                      />
                    </TableCell>
                    <TableCell><strong>Description</strong></TableCell>
                    <TableCell><strong>State</strong></TableCell>
                    <TableCell><strong>Assigned Users</strong></TableCell>
                    <TableCell><strong>Objective Measures</strong></TableCell>
                    <TableCell align="right"><strong>Actions</strong></TableCell>
                  </TableRow>
                </TableHead>
                <TableBody>
                  {paginatedRoleTemplates.map((template) => (
                    <TableRow key={template.id} hover>
                      <TableCell>
                        <Typography variant="body2" fontWeight="medium">
                          {template.name}
                        </Typography>
                      </TableCell>
                      <TableCell>
                        <Typography
                          variant="body2"
                          color="text.secondary"
                          sx={{
                            maxWidth: 400,
                            overflow: 'hidden',
                            textOverflow: 'ellipsis',
                            display: '-webkit-box',
                            WebkitLineClamp: 2,
                            WebkitBoxOrient: 'vertical',
                          }}
                        >
                          {template.description || '—'}
                        </Typography>
                      </TableCell>
                      <TableCell>
                        <Chip
                          label={getStateLabel(template.state)}
                          size="small"
                          color={getStateColor(template.state)}
                          variant="outlined"
                        />
                      </TableCell>
                      <TableCell>
                        <Typography variant="body2" fontWeight="medium">
                          {userCounts[template.id] ?? 0}
                        </Typography>
                      </TableCell>
                      <TableCell>
                        <Chip
                          label={`${template.objectiveMeasures.length} Measure${template.objectiveMeasures.length !== 1 ? 's' : ''}`}
                          size="small"
                          color="primary"
                          variant="outlined"
                        />
                      </TableCell>
                      <TableCell align="right" sx={{ whiteSpace: 'nowrap' }}>
                        <Box sx={{ display: 'inline-flex', gap: 0.5 }}>
                          <Tooltip title="View Objective Measures">
                            <IconButton
                              size="small"
                              onClick={() => {
                                const fullTemplate = roleTemplates.find((t) => t.id === template.id);
                                if (fullTemplate) {
                                  setViewingTemplate(fullTemplate);
                                  setViewMeasuresDialogOpen(true);
                                }
                              }}
                              color="primary"
                            >
                              <VisibilityIcon />
                            </IconButton>
                          </Tooltip>
                          <Tooltip title="More options">
                            <IconButton
                              size="small"
                              onClick={(e) => handleMenuOpen(e, template.id)}
                              disabled={deleting === template.id}
                            >
                              {deleting === template.id ? (
                                <CircularProgress size={20} />
                              ) : (
                                <MoreVertIcon />
                              )}
                            </IconButton>
                          </Tooltip>
                        </Box>
                      </TableCell>
                    </TableRow>
                  ))}
                </TableBody>
              </Table>
            </TableContainer>

            <TablePagination
              component="div"
              count={filteredRoleTemplates.length}
              page={page}
              onPageChange={handlePageChange}
              rowsPerPage={rowsPerPage}
              onRowsPerPageChange={handleRowsPerPageChange}
              rowsPerPageOptions={[10, 25, 50, 100]}
            />
          </>
        )}

      {/* Create Dialog */}
      <Dialog open={createDialogOpen} onClose={() => setCreateDialogOpen(false)} maxWidth="md" fullWidth>
        <DialogTitle>Create Role Template</DialogTitle>
        <DialogContent>
          {createError && (
            <Alert severity="error" sx={{ mb: 2 }} onClose={() => setCreateError('')}>
              {createError}
            </Alert>
          )}
          <TextField
            fullWidth
            label="Name"
            value={createForm.name}
            onChange={(e) => setCreateForm({ ...createForm, name: e.target.value })}
            margin="normal"
            required
          />
          <TextField
            fullWidth
            label="Description"
            value={createForm.description}
            onChange={(e) => setCreateForm({ ...createForm, description: e.target.value })}
            margin="normal"
            multiline
            rows={3}
          />
          <Box sx={{ mt: 3 }}>
            <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', mb: 2 }}>
              <Typography variant="subtitle1" fontWeight="bold">
                Objective Measures
              </Typography>
              <Button
                size="small"
                startIcon={<AddIcon />}
                onClick={handleAddObjectiveMeasure}
                sx={{ textTransform: 'none' }}
              >
                Add Measure
              </Button>
            </Box>
            {createForm.objectiveMeasures.map((measure, index) => (
              <Card key={index} variant="outlined" sx={{ p: 2, mb: 2 }}>
                <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', mb: 2 }}>
                  <Typography variant="subtitle2" fontWeight="bold">
                    Objective Measure {index + 1}
                  </Typography>
                  <IconButton
                    size="small"
                    onClick={() => handleRemoveObjectiveMeasure(index)}
                    color="error"
                  >
                    <DeleteIcon fontSize="small" />
                  </IconButton>
                </Box>
                <TextField
                  fullWidth
                  label="Name"
                  value={measure.name}
                  onChange={(e) => handleObjectiveMeasureChange(index, 'name', e.target.value)}
                  margin="dense"
                  size="small"
                />
                <TextField
                  fullWidth
                  label="Measurement Method"
                  value={measure.measurementMethod}
                  onChange={(e) => handleObjectiveMeasureChange(index, 'measurementMethod', e.target.value)}
                  margin="dense"
                  size="small"
                  multiline
                  minRows={2}
                />
                <TextField
                  fullWidth
                  label="Level 1 Criteria"
                  value={measure.level1Criteria}
                  onChange={(e) => handleObjectiveMeasureChange(index, 'level1Criteria', e.target.value)}
                  margin="dense"
                  size="small"
                  multiline
                  rows={2}
                />
                <TextField
                  fullWidth
                  label="Level 2 Criteria"
                  value={measure.level2Criteria}
                  onChange={(e) => handleObjectiveMeasureChange(index, 'level2Criteria', e.target.value)}
                  margin="dense"
                  size="small"
                  multiline
                  rows={2}
                />
                <TextField
                  fullWidth
                  label="Level 3 Criteria"
                  value={measure.level3Criteria}
                  onChange={(e) => handleObjectiveMeasureChange(index, 'level3Criteria', e.target.value)}
                  margin="dense"
                  size="small"
                  multiline
                  rows={2}
                />
                <TextField
                  fullWidth
                  label="Weighting"
                  type="number"
                  value={measure.weighting}
                  onChange={(e) => {
                    const value = parseFloat(e.target.value) || 0;
                    handleObjectiveMeasureChange(index, 'weighting', value);
                  }}
                  margin="dense"
                  size="small"
                  required
                  inputProps={{ min: 0, max: 1, step: 0.01 }}
                  helperText="Required: Enter a value between 0 and 1 (e.g., 0.25 for 25%)"
                />
              </Card>
            ))}
          </Box>
        </DialogContent>
        <DialogActions>
          <Button onClick={() => setCreateDialogOpen(false)} sx={{ textTransform: 'none' }}>Cancel</Button>
          <Button onClick={handleCreate} variant="contained" disabled={creating} sx={{ textTransform: 'none' }}>
            {creating ? <CircularProgress size={24} /> : 'Create'}
          </Button>
        </DialogActions>
      </Dialog>

      {/* Edit Dialog */}
      <Dialog open={editDialogOpen} onClose={() => setEditDialogOpen(false)} maxWidth="sm" fullWidth>
        <DialogTitle>Edit Role Template</DialogTitle>
        <DialogContent>
          {editError && (
            <Alert severity="error" sx={{ mb: 2 }} onClose={() => setEditError('')}>
              {editError}
            </Alert>
          )}
          <TextField
            fullWidth
            label="Name"
            value={editForm.name}
            onChange={(e) => setEditForm({ ...editForm, name: e.target.value })}
            margin="normal"
            required
          />
          <TextField
            fullWidth
            label="Description"
            value={editForm.description}
            onChange={(e) => setEditForm({ ...editForm, description: e.target.value })}
            margin="normal"
            multiline
            rows={3}
          />
        </DialogContent>
        <DialogActions>
          <Button onClick={() => setEditDialogOpen(false)} sx={{ textTransform: 'none' }}>Cancel</Button>
          <Button onClick={handleEdit} variant="contained" disabled={updating} sx={{ textTransform: 'none' }}>
            {updating ? <CircularProgress size={24} /> : 'Update'}
          </Button>
        </DialogActions>
      </Dialog>

      {/* Add Objective Measure Dialog */}
      <Dialog open={addMeasureDialogOpen} onClose={() => setAddMeasureDialogOpen(false)} maxWidth="sm" fullWidth>
        <DialogTitle>Add Objective Measure</DialogTitle>
        <DialogContent>
          {measureError && (
            <Alert severity="error" sx={{ mb: 2 }} onClose={() => setMeasureError('')}>
              {measureError}
            </Alert>
          )}
          <TextField
            fullWidth
            label="Name"
            value={newMeasure.name}
            onChange={(e) => setNewMeasure({ ...newMeasure, name: e.target.value })}
            margin="normal"
            required
          />
          <TextField
            fullWidth
            label="Measurement Method"
            value={newMeasure.measurementMethod}
            onChange={(e) => setNewMeasure({ ...newMeasure, measurementMethod: e.target.value })}
            margin="normal"
            multiline
            minRows={2}
          />
          <TextField
            fullWidth
            label="Level 1 Criteria"
            value={newMeasure.level1Criteria}
            onChange={(e) => setNewMeasure({ ...newMeasure, level1Criteria: e.target.value })}
            margin="normal"
            multiline
            rows={2}
          />
          <TextField
            fullWidth
            label="Level 2 Criteria"
            value={newMeasure.level2Criteria}
            onChange={(e) => setNewMeasure({ ...newMeasure, level2Criteria: e.target.value })}
            margin="normal"
            multiline
            rows={2}
          />
          <TextField
            fullWidth
            label="Level 3 Criteria"
            value={newMeasure.level3Criteria}
            onChange={(e) => setNewMeasure({ ...newMeasure, level3Criteria: e.target.value })}
            margin="normal"
            multiline
            rows={2}
          />
          <TextField
            fullWidth
            label="Weighting"
            type="number"
            value={newMeasure.weighting}
            onChange={(e) => {
              const value = parseFloat(e.target.value) || 0;
              setNewMeasure({ ...newMeasure, weighting: value });
            }}
            margin="normal"
            required
            inputProps={{ min: 0, max: 1, step: 0.01 }}
            helperText="Required: Enter a value between 0 and 1 (e.g., 0.25 for 25%)"
          />
        </DialogContent>
        <DialogActions>
          <Button onClick={() => setAddMeasureDialogOpen(false)} sx={{ textTransform: 'none' }}>Cancel</Button>
          <Button onClick={handleAddObjectiveMeasureToTemplate} variant="contained" sx={{ textTransform: 'none' }}>
            Add
          </Button>
        </DialogActions>
      </Dialog>

      {/* Edit Objective Measure Dialog */}
      <Dialog open={editMeasureDialogOpen} onClose={() => setEditMeasureDialogOpen(false)} maxWidth="sm" fullWidth>
        <DialogTitle>Edit Objective Measure</DialogTitle>
        <DialogContent>
          {measureError && (
            <Alert severity="error" sx={{ mb: 2 }} onClose={() => setMeasureError('')}>
              {measureError}
            </Alert>
          )}
          <TextField
            fullWidth
            label="Name"
            value={editMeasure.name}
            onChange={(e) => setEditMeasure({ ...editMeasure, name: e.target.value })}
            margin="normal"
            required
          />
          <TextField
            fullWidth
            label="Measurement Method"
            value={editMeasure.measurementMethod}
            onChange={(e) => setEditMeasure({ ...editMeasure, measurementMethod: e.target.value })}
            margin="normal"
            multiline
            minRows={2}
          />
          <TextField
            fullWidth
            label="Level 1 Criteria"
            value={editMeasure.level1Criteria}
            onChange={(e) => setEditMeasure({ ...editMeasure, level1Criteria: e.target.value })}
            margin="normal"
            multiline
            rows={2}
          />
          <TextField
            fullWidth
            label="Level 2 Criteria"
            value={editMeasure.level2Criteria}
            onChange={(e) => setEditMeasure({ ...editMeasure, level2Criteria: e.target.value })}
            margin="normal"
            multiline
            rows={2}
          />
          <TextField
            fullWidth
            label="Level 3 Criteria"
            value={editMeasure.level3Criteria}
            onChange={(e) => setEditMeasure({ ...editMeasure, level3Criteria: e.target.value })}
            margin="normal"
            multiline
            rows={2}
          />
          <TextField
            fullWidth
            label="Weighting"
            type="number"
            value={editMeasure.weighting}
            onChange={(e) => {
              const value = parseFloat(e.target.value) || 0;
              setEditMeasure({ ...editMeasure, weighting: value });
            }}
            margin="normal"
            required
            inputProps={{ min: 0, max: 1, step: 0.01 }}
            helperText="Required: Enter a value between 0 and 1 (e.g., 0.25 for 25%)"
          />
        </DialogContent>
        <DialogActions>
          <Button onClick={() => setEditMeasureDialogOpen(false)} sx={{ textTransform: 'none' }}>Cancel</Button>
          <Button onClick={handleUpdateObjectiveMeasure} variant="contained" sx={{ textTransform: 'none' }}>
            Update
          </Button>
        </DialogActions>
      </Dialog>

      {/* View Objective Measures Dialog */}
      <Dialog
        open={viewMeasuresDialogOpen}
        onClose={() => {
          setViewMeasuresDialogOpen(false);
          setViewingTemplate(null);
        }}
        maxWidth="md"
        fullWidth
        PaperProps={{ sx: { borderRadius: 2 } }}
      >
        <DialogTitle sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', pb: 1 }}>
          <Typography component="span" variant="h6" fontWeight="600">
            {viewingTemplate?.name} - Objective Measures
          </Typography>
          <IconButton
            onClick={() => {
              setViewMeasuresDialogOpen(false);
              setViewingTemplate(null);
            }}
          >
            <CloseIcon />
          </IconButton>
        </DialogTitle>
        <DialogContent>
          {viewingTemplate && (
            <Box sx={{ display: 'flex', flexDirection: 'column', gap: 2, pt: 1 }}>
              {viewingTemplate.description && (
                <Typography variant="body2" color="text.secondary" sx={{ mb: 1 }}>
                  {viewingTemplate.description}
                </Typography>
              )}
              {viewingTemplate.objectiveMeasures.length === 0 ? (
                <Box sx={{ textAlign: 'center', py: 4 }}>
                  <Typography variant="body2" color="text.secondary" gutterBottom>
                    No objective measures defined
                  </Typography>
                </Box>
              ) : (
                <>
                  <Box sx={{ display: 'flex', flexDirection: 'column', gap: 2 }}>
                    {viewingTemplate.objectiveMeasures.map((measure) => (
                      <Card key={measure.id} variant="outlined" sx={{ p: 2 }}>
                        <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'flex-start', flexWrap: 'nowrap', gap: 1, mb: 1 }}>
                          <Box sx={{ minWidth: 0, flex: '1 1 auto' }}>
                            <Typography variant="subtitle2" fontWeight="bold">
                              {measure.name}
                            </Typography>
                            <Typography variant="caption" color="text.secondary" sx={{ display: 'block', mt: 0.5 }}>
                              Weighting: {(measure.weighting * 100).toFixed(0)}%
                            </Typography>
                          </Box>
                          <Box sx={{ flexShrink: 0, display: 'flex' }}>
                            <IconButton
                              size="small"
                              onClick={() => {
                                setViewMeasuresDialogOpen(false);
                                handleOpenEditMeasure(measure);
                              }}
                              color="primary"
                            >
                              <EditIcon fontSize="small" />
                            </IconButton>
                            <IconButton
                              size="small"
                              onClick={() => handleDeleteObjectiveMeasureClick(measure.id)}
                              color="error"
                            >
                              <DeleteIcon fontSize="small" />
                            </IconButton>
                          </Box>
                        </Box>
                        <Box sx={{ mb: 1 }}>
                          <Typography variant="body2" color="text.secondary" component="div">
                            <strong>Measurement Method:</strong>
                          </Typography>
                          <Typography variant="body2" color="text.secondary" sx={{ whiteSpace: 'pre-wrap', mt: 0.5 }}>
                            {measure.measurementMethod}
                          </Typography>
                        </Box>
                        <Grid container spacing={1} sx={{ mt: 1 }}>
                          <Grid size={{ xs: 12, sm: 4 }}>
                            <Typography variant="caption" color="text.secondary">
                              <strong>Level 1:</strong> {measure.level1Criteria}
                            </Typography>
                          </Grid>
                          <Grid size={{ xs: 12, sm: 4 }}>
                            <Typography variant="caption" color="text.secondary">
                              <strong>Level 2:</strong> {measure.level2Criteria}
                            </Typography>
                          </Grid>
                          <Grid size={{ xs: 12, sm: 4 }}>
                            <Typography variant="caption" color="text.secondary">
                              <strong>Level 3:</strong> {measure.level3Criteria}
                            </Typography>
                          </Grid>
                        </Grid>
                      </Card>
                    ))}
                  </Box>
                  <Button
                    size="small"
                    startIcon={<AddIcon />}
                    onClick={() => {
                      setViewMeasuresDialogOpen(false);
                      handleOpenAddMeasure(viewingTemplate.id);
                    }}
                    sx={{ textTransform: 'none', alignSelf: 'flex-start' }}
                  >
                    Add Objective Measure
                  </Button>
                </>
              )}
            </Box>
          )}
        </DialogContent>
        <DialogActions sx={{ p: 2.5, pt: 1 }}>
          <Button
            onClick={() => {
              setViewMeasuresDialogOpen(false);
              setViewingTemplate(null);
            }}
            sx={{ textTransform: 'none' }}
          >
            Close
          </Button>
        </DialogActions>
      </Dialog>

      {/* Delete Confirmation Dialog */}
      <Dialog open={deleteDialogOpen} onClose={() => { setDeleteDialogOpen(false); setTemplateToDelete(null); }} maxWidth="sm" fullWidth>
        <DialogTitle>
          {templateToDelete && (userCounts[templateToDelete.id] ?? 0) > 0
            ? 'Cannot Delete Role Template'
            : 'Delete Role Template'}
        </DialogTitle>
        <DialogContent>
          {templateToDelete && (() => {
            const assignedUserCount = userCounts[templateToDelete.id] ?? 0;
            if (assignedUserCount > 0) {
              return (
                <Alert severity="error" sx={{ mb: 2 }}>
                  <Typography variant="body1" fontWeight="600" gutterBottom>
                    This role template cannot be deleted because it is currently assigned to <strong>{assignedUserCount}</strong> user{assignedUserCount !== 1 ? 's' : ''}.
                  </Typography>
                  <Typography variant="body2">
                    Please unassign all users from the template "{templateToDelete.name}" before attempting to delete it. You can do this by changing the role template for each assigned user on the User Management page.
                  </Typography>
                </Alert>
              );
            }
            return (
              <>
                <Typography variant="body1" sx={{ mb: 2 }}>
                  Are you sure you want to delete the role template <strong>"{templateToDelete.name}"</strong>?
                </Typography>
                <Alert severity="warning">
                  This action cannot be undone. All objective measures associated with this template will also be deleted.
                </Alert>
              </>
            );
          })()}
        </DialogContent>
        <DialogActions>
          <Button onClick={() => { setDeleteDialogOpen(false); setTemplateToDelete(null); }} sx={{ textTransform: 'none' }}>
            {templateToDelete && (userCounts[templateToDelete.id] ?? 0) > 0 ? 'Close' : 'Cancel'}
          </Button>
          {templateToDelete && (userCounts[templateToDelete.id] ?? 0) === 0 && (
            <Button
              onClick={handleDeleteConfirm}
              variant="contained"
              color="error"
              disabled={deleting === templateToDelete.id}
              sx={{ textTransform: 'none' }}
            >
              {deleting === templateToDelete.id ? <CircularProgress size={20} /> : 'Delete'}
            </Button>
          )}
        </DialogActions>
      </Dialog>

      {/* Delete Objective Measure Confirmation Dialog */}
      <Dialog
        open={deleteMeasureDialogOpen}
        onClose={() => {
          setDeleteMeasureDialogOpen(false);
          setMeasureToDelete(null);
        }}
        maxWidth="sm"
        fullWidth
      >
        <DialogTitle>Delete Objective Measure</DialogTitle>
        <DialogContent>
          <Typography variant="body1">
            Are you sure you want to delete this objective measure? This action cannot be undone.
          </Typography>
        </DialogContent>
        <DialogActions>
          <Button
            onClick={() => {
              setDeleteMeasureDialogOpen(false);
              setMeasureToDelete(null);
            }}
            sx={{ textTransform: 'none' }}
          >
            Cancel
          </Button>
          <Button
            onClick={handleDeleteObjectiveMeasure}
            variant="contained"
            color="error"
            sx={{ textTransform: 'none' }}
          >
            Delete
          </Button>
        </DialogActions>
      </Dialog>

      {/* Apply objective weights to reviews */}
      <Dialog
        open={applyWeightsDialogOpen}
        onClose={() => !applyingWeights && setApplyWeightsDialogOpen(false)}
        maxWidth="sm"
        fullWidth
      >
        <DialogTitle>Apply weights to reviews</DialogTitle>
        <DialogContent>
          <Typography variant="body2" sx={{ mb: 2 }}>
            Copy the current objective weightings from{' '}
            <strong>{applyWeightsTemplate?.name ?? 'this role template'}</strong> onto matching
            reviews in the selected cycle, then recalculate scores. Confirm the template weightings
            are correct before continuing. Employees and managers on affected reviews will be emailed
            that their scores may have changed.
          </Typography>
          {applyWeightsError && (
            <Alert severity="error" sx={{ mb: 2 }} onClose={() => setApplyWeightsError('')}>
              {applyWeightsError}
            </Alert>
          )}
          <FormControl fullWidth sx={{ mb: 2 }}>
            <InputLabel id="apply-weights-cycle-label">Review cycle</InputLabel>
            <Select
              labelId="apply-weights-cycle-label"
              label="Review cycle"
              value={applyWeightsCycleId}
              onChange={(e) => setApplyWeightsCycleId(e.target.value)}
              disabled={applyingWeights}
            >
              {reviewCycles.map((cycle) => (
                <MenuItem key={cycle.id} value={cycle.id}>
                  {cycle.name}
                </MenuItem>
              ))}
            </Select>
          </FormControl>
          <FormControlLabel
            control={
              <Checkbox
                checked={includeClosedReviews}
                onChange={(e) => setIncludeClosedReviews(e.target.checked)}
                disabled={applyingWeights}
              />
            }
            label="Include closed reviews (finalised, excluded, and not submitted)"
          />
          {includeClosedReviews && (
            <Alert severity="warning" sx={{ mt: 1 }}>
              Closed reviews will have their stored scores updated. Final ratings may change.
              Employees and managers will still be emailed about the update.
            </Alert>
          )}
        </DialogContent>
        <DialogActions>
          <Button
            onClick={() => setApplyWeightsDialogOpen(false)}
            disabled={applyingWeights}
            sx={{ textTransform: 'none' }}
          >
            Cancel
          </Button>
          <Button
            onClick={() => void handleApplyWeightsConfirm()}
            variant="contained"
            disabled={applyingWeights || !applyWeightsCycleId}
            sx={{ textTransform: 'none' }}
          >
            {applyingWeights ? 'Applying…' : 'Apply and recalculate'}
          </Button>
        </DialogActions>
      </Dialog>

      {/* Actions Menu */}
      <Menu anchorEl={menuAnchor} open={Boolean(menuAnchor)} onClose={handleMenuClose}>
        {(() => {
          const template = roleTemplates.find((t) => t.id === menuTemplateId);
          if (!template) return null;
          
          const state = typeof template.state === 'string' 
            ? template.state 
            : template.state === RoleTemplateState.Draft 
              ? 'Draft' 
              : template.state === RoleTemplateState.PendingApproval 
                ? 'PendingApproval' 
                : 'Approved';
          
          const menuItems = [];
          
          if (state === 'Draft') {
            menuItems.push(
              <MenuItem
                key="submit"
                onClick={() => {
                  if (menuTemplateId) handleSubmitForApproval(menuTemplateId);
                }}
              >
                <SendIcon sx={{ mr: 1, fontSize: 20 }} /> Submit for Approval
              </MenuItem>
            );
          }
          
          if (state === 'PendingApproval') {
            menuItems.push(
              <MenuItem
                key="approve"
                onClick={() => {
                  if (menuTemplateId) handleApprove(menuTemplateId);
                }}
                sx={{ color: 'success.main' }}
              >
                <CheckCircleIcon sx={{ mr: 1, fontSize: 20 }} /> Approve
              </MenuItem>
            );
          }
          
          menuItems.push(
            <MenuItem
              key="edit"
              onClick={() => {
                if (template) handleOpenEdit(template);
              }}
            >
              <EditIcon sx={{ mr: 1, fontSize: 20 }} /> Edit
            </MenuItem>
          );

          menuItems.push(
            <MenuItem
              key="apply-weights"
              onClick={() => {
                if (template) void handleOpenApplyWeights(template);
              }}
            >
              <BalanceIcon sx={{ mr: 1, fontSize: 20 }} /> Apply weights to reviews
            </MenuItem>
          );
          
          menuItems.push(
            <MenuItem
              key="delete"
              onClick={() => {
                if (menuTemplateId) handleDeleteClick(menuTemplateId);
              }}
              sx={{ color: 'error.main' }}
            >
              <DeleteIcon sx={{ mr: 1, fontSize: 20 }} /> Delete
            </MenuItem>
          );
          
          return menuItems;
        })()}
      </Menu>
    </Box>
    </AppLayout>
  );
}

